/** @jsx jsx */
/** @jsxRuntime classic */

import React from "react";
import { jsx } from "@emotion/core";
import classNames from "classnames";

type Scores = {
  ratingValue: number;
  fractionOfRatings: number;
};

type BoundaryZone = {
  from: number | string;
  to: number | string;
  colour: string;
  label?: string;
};

type HeaderConfig = {
  // New boundary format
  boundaries?: BoundaryZone[];
  // Legacy format
  aboveThreshold?: number;
  aboveColour?: string;
  betweenUpper?: number;
  betweenLower?: number;
  betweenColour?: string;
  belowThreshold?: number;
  belowColour?: string;
  naColour?: string;
};

type SummaryCardProps = {
  site: string;
  reviews: number;
  breakdown: Scores[];
  href?: string | null;
  headerConfig?: HeaderConfig;
};

const SummaryCard: React.FC<SummaryCardProps> = ({ site, reviews, breakdown, href, headerConfig }) => {
  function calculateAverage() {
    if (reviews === 0 || !breakdown) return "N/A";
    const num = reviews / 100;
    let score = 1;
    let sum = 0;

    breakdown.forEach((e) => {
      const percentage = e.fractionOfRatings * 100;
      const ratings = num * percentage;
      const total = ratings * score;
      score = score + 1;
      sum = sum + total;
    });

    return (sum / reviews).toFixed(2);
  }

  const avg = parseFloat(calculateAverage());

  const hasAnyColourConfig =
    headerConfig &&
    (headerConfig.boundaries ||
     headerConfig.aboveColour || headerConfig.betweenColour || headerConfig.belowColour || headerConfig.naColour || headerConfig.aboveThreshold || headerConfig.betweenUpper || headerConfig.betweenLower || headerConfig.belowThreshold);

  const getConfiguredColour = (): string | undefined => {
    if (!headerConfig) return undefined;
    if (isNaN(avg)) return headerConfig.naColour;

    // New boundary format
    if (headerConfig.boundaries && headerConfig.boundaries.length > 0) {
      // Sort by 'from' ascending so we can iterate from highest to lowest zone
      // This ensures a score landing on a shared boundary (e.g. 4 in zones 3-4 and 4-5) gets the higher zone
      const sortedBoundaries = [...headerConfig.boundaries].sort((a, b) => {
        const aFrom = parseFloat(String(a.from));
        const bFrom = parseFloat(String(b.from));
        return aFrom - bFrom;
      });
      for (let i = sortedBoundaries.length - 1; i >= 0; i--) {
        const zone = sortedBoundaries[i];
        const from = parseFloat(String(zone.from));
        const to = parseFloat(String(zone.to));
        if (!isNaN(from) && !isNaN(to) && avg >= from && avg <= to) {
          return zone.colour;
        }
      }
      // Fallback: use first or last zone if score is at exact boundary
      const first = sortedBoundaries[0];
      const last = sortedBoundaries[sortedBoundaries.length - 1];
      if (avg <= parseFloat(String(first.to))) return first.colour;
      if (avg >= parseFloat(String(last.from))) return last.colour;
      return headerConfig.naColour;
    }

    // Legacy format
    if (headerConfig.aboveThreshold != null && avg >= headerConfig.aboveThreshold) return headerConfig.aboveColour;
    if (headerConfig.belowThreshold != null && avg < headerConfig.belowThreshold) return headerConfig.belowColour;
    return headerConfig.betweenColour;
  };

  const getHeaderColour = () => {
    switch (true) {
      case avg > 0 && avg <= 1:
        return "VeryNegativeHeader";
      case avg > 1 && avg <= 2:
        return "NegativeHeader";
      case avg > 2 && avg <= 3:
        return "NeutralHeader";
      case avg > 3 && avg <= 4:
        return "PositiveHeader";
      case avg > 4 && avg <= 5:
        return "VeryPositiveHeader";
      default:
        return "NotApplicable";
    }
  };

  let headerDiv: JSX.Element;
  if (hasAnyColourConfig) {
    const bgColour = getConfiguredColour();
    headerDiv = <div className="SummaryCardHeader" style={{ backgroundColor: bgColour }}></div>;
  } else {
    const headerColour = getHeaderColour();
    const className = classNames("SummaryCardHeader", headerColour);
    headerDiv = <div className={className}></div>;
  }

  const boundaryBar = (() => {
    if (!headerConfig?.boundaries || headerConfig.boundaries.length === 0) return null;

    const sortedZones = headerConfig.boundaries
      .map((z) => ({
        ...z,
        fromN: parseFloat(String(z.from)),
        toN: parseFloat(String(z.to)),
      }))
      .filter((z) => !isNaN(z.fromN) && !isNaN(z.toN) && z.toN > z.fromN)
      .sort((a, b) => a.fromN - b.fromN);

    if (sortedZones.length === 0) return null;

    const max = sortedZones[sortedZones.length - 1].toN;

    // Zoom the bar's visible range to focus on where scores actually live.
    // We start just below the lowest cutoff between bands (the lowest "from"
    // that isn't the bottom band's start), leaving a thin sliver of the bottom
    // band visible as a visual hint. Fall back to the full range if there's
    // only one band (no cutoff exists).
    const min =
      sortedZones.length >= 2
        ? sortedZones[1].fromN - 0.1
        : sortedZones[0].fromN;
    const totalRange = max - min;
    if (totalRange <= 0) return null;

    const markerPct =
      !isNaN(avg) ? Math.max(0, Math.min(100, ((avg - min) / totalRange) * 100)) : null;

    const currentZoneIdx = !isNaN(avg)
      ? sortedZones.findIndex((z, i) => {
          const isLast = i === sortedZones.length - 1;
          return avg >= z.fromN && (isLast ? avg <= z.toN : avg < z.toN);
        })
      : -1;

    return (
      <div className="SummaryCardBoundaryBar">
        <div className="SummaryCardBoundaryBarSegments">
          {sortedZones
            .slice()
            .reverse()
            .map((zone, i) => {
              // Clip each zone to the visible range. Zones entirely below the
              // visible range render as 0 height; the bottom zone usually gets
              // cropped down to a thin sliver (~0.1 / totalRange).
              const visibleTop = Math.min(zone.toN, max);
              const visibleBottom = Math.max(zone.fromN, min);
              const visibleSpan = Math.max(0, visibleTop - visibleBottom);
              const heightPct = (visibleSpan / totalRange) * 100;
              if (heightPct <= 0) return null;
              return (
                <div
                  key={i}
                  className="SummaryCardBoundaryBarSegment"
                  style={{ height: `${heightPct}%`, backgroundColor: zone.colour }}
                />
              );
            })}
        </div>
        {markerPct != null && (
          <div
            className="SummaryCardBoundaryBarMarker"
            style={{ bottom: `${markerPct}%` }}
            aria-hidden="true"
          />
        )}
        <div className="SummaryCardBoundaryBarTooltip" role="tooltip">
          <div className="SummaryCardBoundaryBarTooltipTitle">Score boundaries</div>
          <ul>
            {sortedZones
              .slice()
              .reverse()
              .map((zone, displayIdx) => {
                const originalIdx = sortedZones.length - 1 - displayIdx;
                const isCurrent = originalIdx === currentZoneIdx;
                return (
                  <li
                    key={originalIdx}
                    className={isCurrent ? "is-current" : undefined}
                  >
                    <span
                      className="SummaryCardBoundaryBarTooltipSwatch"
                      style={{ backgroundColor: zone.colour }}
                    />
                    <span className="SummaryCardBoundaryBarTooltipRange">
                      {zone.fromN}–{zone.toN}
                    </span>
                    {zone.label && (
                      <span className="SummaryCardBoundaryBarTooltipLabel">
                        {zone.label}
                      </span>
                    )}
                    {isCurrent && (
                      <span className="SummaryCardBoundaryBarTooltipBadge">
                        {avg.toFixed(2)}
                      </span>
                    )}
                  </li>
                );
              })}
          </ul>
        </div>
      </div>
    );
  })();

  const content = (
    <React.Fragment>
      {headerDiv}
      <div className="SummaryCardBody">
        <div className="SummaryCardBodyContent">
          <div className="SummaryCardSiteScore">
            <h6>{site}</h6>
            <p className="Figure">{calculateAverage()}</p>
          </div>
          <div className="SummaryCardReviewTotal">
            <h6>Reviews</h6>
            <p className="Figure">{reviews.toLocaleString("en-GB")}</p>
          </div>
        </div>
        {boundaryBar}
      </div>
      <div className="SummaryCardFooter">
        <h6 className="BreakdownCaption GreyTxt">Score breakdown</h6>
        <div className="BreakdownFlex">
          <div>
            <p className="GreyTxt">1</p>
            <p className="VeryNegative">{(breakdown[0].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">2</p>
            <p className="Negative">{(breakdown[1].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">3</p>
            <p className="Neutral">{(breakdown[2].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">4</p>
            <p className="Positive">{(breakdown[3].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
          <div>
            <p className="GreyTxt">5</p>
            <p className="VeryPositive">{(breakdown[4].fractionOfRatings * 100).toFixed(1)}%</p>
          </div>
        </div>
      </div>
    </React.Fragment>
  );

  if (href)
    return (
      <a href={href} className="SummaryCard">
        {content}
      </a>
    );

  return <div className="SummaryCard">{content}</div>;
};

export default SummaryCard;
